// Enhanced SEO utilities for better keyword distribution and structured data

export const addKeywordRichContent = () => {
  // Add keyword-rich content to improve SEO
  const keywordElements = document.querySelectorAll('[data-seo-keywords]');
  
  keywordElements.forEach(element => {
    const keywords = element.getAttribute('data-seo-keywords').split(',');
    const content = element.textContent;
    
    // Ensure keywords are naturally distributed in content
    keywords.forEach(keyword => {
      if (!content.toLowerCase().includes(keyword.toLowerCase().trim())) {
        console.warn(`Keyword "${keyword}" not found in content: ${content.substring(0, 50)}...`);
      }
    });
  });
};

export const generatePageSpecificStructuredData = (pageType, pageData) => {
  let structuredData = {};
  
  switch (pageType) {
    case 'homepage':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Radix Technologies",
        "alternateName": "Radix Techs",
        "url": "https://radixtechs.com",
        "logo": "https://radixtechs.com/images/logo.webp",
        "description": "Expert IT services & cloud solutions. 19+ years experience, 164+ projects delivered. DevOps, AI development, mobile apps & managed services.",
        "foundingDate": "2015",
        "numberOfEmployees": "50-100",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "West Bank",
          "addressCountry": "Palestine"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+972-92337772",
          "email": "info@radixtechs.com",
          "contactType": "Customer Service",
          "availableLanguage": ["English", "Arabic"]
        },
        "sameAs": [
          "https://linkedin.com/company/radix-techs",
          "https://facebook.com/RadixTechs",
          "https://twitter.com/RadixTechs"
        ],
        "serviceType": [
          "DevOps Automation",
          "Cloud Migration",
          "AI Development",
          "Mobile Development",
          "Quality Assurance",
          "Managed Services"
        ],
        "areaServed": "Worldwide",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "IT Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Development Services",
                "description": "Cutting-edge AI solutions to transform business operations and drive innovation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "DevOps & Cloud Solutions",
                "description": "Scalable infrastructure and automated deployment pipelines for modern applications"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Mobile Development",
                "description": "Native and cross-platform mobile applications for iOS and Android"
              }
            }
          ]
        }
      };
      break;
      
    case 'services':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": pageData.serviceName || "IT Services",
        "description": pageData.description || "Comprehensive IT services including DevOps automation, cloud migration, AI development, and mobile apps.",
        "provider": {
          "@type": "Organization",
          "name": "Radix Technologies",
          "url": "https://radixtechs.com"
        },
        "areaServed": "Worldwide",
        "serviceType": "Information Technology Services",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceRange": "Contact for pricing"
        }
      };
      break;
      
    case 'about':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Radix Technologies",
        "description": "Learn about Radix Technologies - 19+ years IT excellence, 164+ successful projects, 50+ happy clients.",
        "url": "https://radixtechs.com/about",
        "mainEntity": {
          "@type": "Organization",
          "name": "Radix Technologies",
          "foundingDate": "2005",
          "description": "AI-driven IT development company specializing in cloud solutions, DevOps automation, and comprehensive IT services."
        }
      };
      break;
      
    default:
      structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": pageData.title,
        "description": pageData.description,
        "url": pageData.url,
        "isPartOf": {
          "@type": "WebSite",
          "name": "Radix Technologies",
          "url": "https://radixtechs.com"
        }
      };
  }
  
  // Remove existing page-specific structured data
  const existingScript = document.getElementById(`structured-data-${pageType}`);
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = `structured-data-${pageType}`;
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

export const optimizeHeadingStructure = () => {
  // Ensure proper heading hierarchy
  const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
  let hasH1 = false;
  let hasH2 = false;
  
  headings.forEach(heading => {
    if (heading.tagName === 'H1') {
      hasH1 = true;
    }
    if (heading.tagName === 'H2') {
      hasH2 = true;
    }
  });
  
  if (!hasH1) {
    console.warn('SEO Warning: No H1 tag found on page');
  }
  
  if (!hasH2) {
    console.warn('SEO Warning: No H2 tags found on page');
  }
  
  return { hasH1, hasH2, totalHeadings: headings.length };
};

export const addInternalLinkingStructure = () => {
  // Add contextual internal links to improve SEO
  const contentSections = document.querySelectorAll('.section');
  
  contentSections.forEach(section => {
    const sectionId = section.id;
    if (sectionId) {
      // Add anchor links for better navigation
      const heading = section.querySelector('h2, h3');
      if (heading && !heading.querySelector('.anchor-link')) {
        const anchorLink = document.createElement('a');
        anchorLink.href = `#${sectionId}`;
        anchorLink.className = 'anchor-link';
        anchorLink.setAttribute('aria-label', `Link to ${heading.textContent}`);
        anchorLink.innerHTML = '#';
        anchorLink.style.cssText = `
          opacity: 0;
          margin-left: 0.5rem;
          color: var(--color-primary-blue-500);
          text-decoration: none;
          font-weight: normal;
          transition: opacity 0.3s ease;
        `;
        
        heading.appendChild(anchorLink);
        
        // Show anchor link on hover
        heading.addEventListener('mouseenter', () => {
          anchorLink.style.opacity = '1';
        });
        
        heading.addEventListener('mouseleave', () => {
          anchorLink.style.opacity = '0';
        });
      }
    }
  });
};

export const validateSEORequirements = () => {
  const results = {
    title: {
      exists: !!document.title,
      length: document.title.length,
      optimal: document.title.length >= 30 && document.title.length <= 60
    },
    description: {
      exists: false,
      length: 0,
      optimal: false
    },
    h1: {
      exists: false,
      count: 0,
      optimal: false
    },
    h2: {
      exists: false,
      count: 0
    },
    internalLinks: {
      count: 0
    },
    keywords: {
      inTitle: false,
      inDescription: false,
      inHeadings: false
    }
  };
  
  // Check meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    results.description.exists = true;
    results.description.length = metaDescription.content.length;
    results.description.optimal = results.description.length >= 120 && results.description.length <= 160;
  }
  
  // Check headings
  const h1Tags = document.querySelectorAll('h1');
  const h2Tags = document.querySelectorAll('h2');
  
  results.h1.exists = h1Tags.length > 0;
  results.h1.count = h1Tags.length;
  results.h1.optimal = h1Tags.length === 1;
  
  results.h2.exists = h2Tags.length > 0;
  results.h2.count = h2Tags.length;
  
  // Check internal links
  const internalLinks = document.querySelectorAll('a[href^="/"], a[href^="#"]');
  results.internalLinks.count = internalLinks.length;
  
  // Check keyword distribution
  const title = document.title.toLowerCase();
  const description = metaDescription ? metaDescription.content.toLowerCase() : '';
  const headingText = Array.from(document.querySelectorAll('h1, h2, h3')).map(h => h.textContent.toLowerCase()).join(' ');
  
  const keywords = ['it services', 'cloud solutions', 'devops', 'ai development', 'mobile development'];
  
  results.keywords.inTitle = keywords.some(keyword => title.includes(keyword));
  results.keywords.inDescription = keywords.some(keyword => description.includes(keyword));
  results.keywords.inHeadings = keywords.some(keyword => headingText.includes(keyword));
  
  return results;
};

// Initialize SEO enhancements
export const initializeSEOEnhancements = (pageType = 'default', pageData = {}) => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        addKeywordRichContent();
        generatePageSpecificStructuredData(pageType, pageData);
        optimizeHeadingStructure();
        addInternalLinkingStructure();
      }, 100);
    });
  } else {
    setTimeout(() => {
      addKeywordRichContent();
      generatePageSpecificStructuredData(pageType, pageData);
      optimizeHeadingStructure();
      addInternalLinkingStructure();
    }, 100);
  }
};
